import * as React from 'react';

import { utcToZonedTime, format as formatDate } from 'date-fns-tz';
import { isBefore } from 'date-fns';
import { getLocalTimeZone, getTimezoneCode } from 'utils';

import { JsonObject, Timezone } from '@bb/domain/dist/types';
import { Text, ListingDetailsComponents } from 'components';
import { AuthContext } from 'contexts';
import { ListingDoc } from '@bb/domain/dist/schemas';

type OpenHouseFormType = {
  date: string | Date;
  timeFrom: string | Date;
  timeTo: string | Date;
  type: 'agent' | 'public';
};

type PropertyInfoProps = {
  listing: JsonObject<
    Omit<ListingDoc, 'org' | 'reps' | 'openHouses'> & {
      openHouses: OpenHouseFormType[];
    }
  >;
  attachments: ListingDetailsComponents.PublicAttachmentType[];
  offerInfo?: React.ReactNode;
  enableOpenHouses: boolean;
};

const isFutureOpenHouse = ({
  timeTo,
  timezone,
}: {
  timeTo: string | Date;
  timezone: Timezone;
}) => {
  const ohDate = utcToZonedTime(new Date(timeTo), timezone);
  return isBefore(new Date(), ohDate);
};

const PropertyInfo: React.FunctionComponent<PropertyInfoProps> = ({
  listing,
  attachments,
  offerInfo,
  enableOpenHouses,
}) => {
  const { auth } = React.useContext(AuthContext);
  const localTimezone = getLocalTimeZone(auth);
  const { timezone } = listing;
  return (
    <div
      className={`col-md-6 listing-details-component-property`}
      style={{ height: '100%' }}
    >
      <Text strong>About This Property:</Text>
      <p className="listing-details-box">{listing.client_remarks}</p>
      {listing.brokerage_remarks && (
        <>
          <Text strong>Brokerage Remarks:</Text>
          <p className="listing-details-box">{listing.brokerage_remarks}</p>
        </>
      )}
      {offerInfo}
      {attachments?.length > 0 && (
        <ListingDetailsComponents.PublicAttachments attachments={attachments} />
      )}
      {enableOpenHouses &&
        listing.openHouses &&
        listing.openHouses.length > 0 && (
          <>
            <Text strong>Open Houses:</Text>
            <div>
              <Text>
                Times reflect the {timezone ? 'listing' : 'user'}&apos;s
                timezone ({getTimezoneCode(listing.timezone || localTimezone)})
              </Text>
            </div>

            <table className="table open-house-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Date</th>
                  <th>From</th>
                  <th>To</th>
                </tr>
              </thead>
              <tbody>
                {listing.openHouses.map((openHouse, index) => {
                  // This is due to existing open houses being in string format and
                  // newly added open house from the create/edit listing page being in moment
                  // which might require timezone conversion
                  const { timeFrom, timeTo } = openHouse;

                  if (
                    !timeFrom ||
                    !timeTo ||
                    !isFutureOpenHouse({
                      timeTo,
                      timezone: timezone || localTimezone,
                    })
                  ) {
                    return null;
                  }

                  const dateStr = formatDate(new Date(timeFrom), 'MMM d, yyyy');
                  const fromStr = formatDate(new Date(timeFrom), 'h:mm a');
                  const toStr = formatDate(new Date(timeTo), 'h:mm a');

                  return (
                    <tr key={index}>
                      <td>{openHouse.type}</td>
                      <td>{dateStr}</td>
                      <td>{fromStr}</td>
                      <td>{toStr}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
    </div>
  );
};

export default PropertyInfo;
